import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../../api';
import PhotoGallery from '../components/PhotoGallery';
import TimeInQueue from '../components/TimeInQueue';
import { formatDateTime } from '../../utils/dateUtils';
import { useAuth } from '../../context/AuthContext';
import { useIsMobile, MobilePage, StickyAction, Icon } from '../../mobile';
import ResolveCallModal from '../components/ResolveCallModal';

const STATUS_COLORS = { Open: '#3b82f6', Claimed: '#f59e0b', Resolved: '#10b981' };

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  dispatcher: 'Dispatcher',
  sales: 'Sales',
  driver: 'Driver',
};

const ROLE_COLORS = {
  super_admin: '#7c3aed',
  admin: '#2563eb',
  dispatcher: '#0891b2',
  sales: '#d97706',
  driver: '#059669',
};

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr.replace(' ', 'T') + (dateStr.includes('Z') ? '' : 'Z'));
  const now = new Date();
  const diffMs = now - date;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export default function CallDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const isMobile = useIsMobile();
  const [call, setCall] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showResolve, setShowResolve] = useState(false);
  const [resolutionReasons, setResolutionReasons] = useState([]);
  const [claiming, setClaiming] = useState(false);

  // Comment state
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [showOtherInput, setShowOtherInput] = useState(false);
  const [sending, setSending] = useState(false);
  const threadEndRef = useRef(null);

  // Get current user from JWT
  const [currentUser, setCurrentUser] = useState(null);
  useEffect(() => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setCurrentUser(payload);
      }
    } catch (e) { /* ignore */ }
  }, []);

  const loadCall = useCallback(() => api.getCall(id).then(setCall).catch(() => navigate('/')), [id, navigate]);
  useEffect(() => { loadCall().finally(() => setLoading(false)); }, [loadCall]);
  useEffect(() => {
    if (!isMobile) return;
    api.getResolutionReasons().then(setResolutionReasons).catch(() => {});
  }, [isMobile]);

  async function handleClaim() {
    setClaiming(true);
    try { await api.claimCall(id); await loadCall(); }
    catch (err) { alert(err.message); }
    setClaiming(false);
  }

  const loadComments = useCallback(() => {
    api.getCallComments(id).then(setComments).catch(() => {});
  }, [id]);

  // Load comments on mount and poll every 15 seconds
  useEffect(() => {
    loadComments();
    const interval = setInterval(loadComments, 15000);
    return () => clearInterval(interval);
  }, [loadComments]);

  // Scroll to bottom when new comments arrive
  useEffect(() => {
    if (threadEndRef.current) {
      threadEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [comments.length]);

  async function sendComment(message) {
    if (!message || !message.trim() || sending) return;
    setSending(true);
    try {
      const newComment = await api.addCallComment(id, message.trim());
      setComments(prev => [...prev, newComment]);
      setCommentText('');
      setShowOtherInput(false);
    } catch (err) {
      alert(err.message || 'Failed to send message');
    }
    setSending(false);
  }

  function handlePreset(msg) {
    sendComment(msg);
  }

  function handleSendOther(e) {
    e.preventDefault();
    sendComment(commentText);
  }

  if (loading) return <div className="loading">Loading...</div>;
  if (!call) return null;

  const isDriver = (currentUser?.team_name || '') === 'Driver Operations' || currentUser?.role === 'driver';
  const isMyClaimedCall = isDriver && call.claimed_by === currentUser?.id;
  const canComment = call.status !== 'Resolved' && (
    !isDriver || isMyClaimedCall
  );

  const goBack = () => (window.history.state?.idx > 0 ? navigate(-1) : navigate('/'));

  // ── Communication thread (shared by both layouts) ───────────────────
  const thread = (
    <>
      {comments.length === 0 && <p className="comment-empty">No messages yet.</p>}
      {comments.length > 0 && (
        <div className="comment-thread">
          {comments.map(c => {
            const isMe = c.user_id === currentUser?.id;
            return (
              <div key={c.id} className={`comment-bubble ${isMe ? 'comment-mine' : ''} ${c.role === 'driver' ? 'comment-driver' : ''}`}>
                <div className="comment-header">
                  <span className="comment-author">{c.full_name}</span>
                  <span className="comment-role-badge" style={{ background: ROLE_COLORS[c.role] || '#6b7280' }}>
                    {ROLE_LABELS[c.role] || c.role}
                  </span>
                  <span className="comment-time">{timeAgo(c.created_at)}</span>
                </div>
                <div className="comment-message">{c.is_preset ? <strong>{c.message}</strong> : c.message}</div>
              </div>
            );
          })}
          <div ref={threadEndRef} />
        </div>
      )}
    </>
  );

  // ── Phone layout ────────────────────────────────────────────────────
  // What a driver needs, in order: where is it (Directions), what is it,
  // who to call, then the thread. The next step — Claim, or Resolve when
  // it is mine — is pinned above the tab bar and never scrolls away.
  if (isMobile) {
    const isMine = call.claimed_by === currentUser?.id;
    const isDueTimePending = call.due_time && new Date(call.due_time.replace(' ', 'T') + 'Z').getTime() > Date.now();
    const dueTimeDisplay = call.due_time
      ? new Date(call.due_time.replace(' ', 'T') + 'Z').toLocaleTimeString('en-US', { timeZone: 'America/Chicago', hour: 'numeric', minute: '2-digit' })
      : null;
    const canClaim = call.status === 'Open' && can('claim_calls');
    const canResolve = call.status !== 'Resolved' && (
      (call.status === 'Claimed' && (isMine || can('resolve_calls'))) || (call.status === 'Open' && !isDriver && can('resolve_calls')));
    const mapsUrl = call.location ? `https://maps.apple.com/?q=${encodeURIComponent([call.location, call.property_name].filter(Boolean).join(', '))}` : null;
    const statusColor = { Open: { bg: '#eff6ff', fg: '#2563eb' }, Claimed: { bg: '#fffbeb', fg: '#b45309' }, Resolved: { bg: '#f0fdf4', fg: '#15803d' } }[call.status] || { bg: '#eff6ff', fg: '#2563eb' };
    const Row = ({ label, value, href }) => value ? (
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, minHeight: 44, alignItems: 'center', borderTop: '1px solid var(--border-subtle)' }}>
        <span style={{ fontSize: 13, color: 'var(--text-muted)', flexShrink: 0 }}>{label}</span>
        {href
          ? <a href={href} style={{ fontSize: 15, fontWeight: 600, color: 'var(--brand-accent)', textAlign: 'right' }}>{value}</a>
          : <span style={{ fontSize: 15, fontWeight: 600, textAlign: 'right', wordBreak: 'break-word' }}>{value}</span>}
      </div>
    ) : null;
    const presetBtn = (label, msg) => (
      <button type="button" className="m-btn m-btn-secondary" onClick={() => handlePreset(msg)} disabled={sending}>{label}</button>
    );
    return (
      <MobilePage
        title={call.property_name || call.call_type_name || `Call #${call.id}`}
        subtitle={`Call #${call.id} · ${call.region}${call.call_type_name && call.property_name ? ` · ${call.call_type_name}` : ''}`}
        side={(
          <>
            <button type="button" className="m-btn-icon sm" aria-label="Back" onClick={goBack}><Icon name="back" size={20} /></button>
            <span className="m-pill" style={{ background: statusColor.bg, color: statusColor.fg }}>{call.status.toUpperCase()}{isMine && call.status === 'Claimed' ? ' · MINE' : ''}</span>
          </>
        )}>
        {call.is_emergency ? (
          <div className="m-note" style={{ background: '#fef2f2', color: '#991b1b', marginBottom: 10 }}><Icon name="alert" size={16} color="#dc2626" /> Emergency call</div>
        ) : null}

        <div className="m-card" style={{ marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 17, fontWeight: 700, lineHeight: 1.3 }}>{call.location}</div>
              {call.space_number && <div style={{ fontSize: 14, color: 'var(--text-secondary)', marginTop: 2 }}>Space #{call.space_number}</div>}
              {dueTimeDisplay && <div style={{ fontSize: 13, color: '#b45309', fontWeight: 600, marginTop: 4 }}>Due at {dueTimeDisplay} CT</div>}
            </div>
            {mapsUrl && (
              <a href={mapsUrl} target="_blank" rel="noreferrer" className="m-btn-icon" aria-label="Directions" style={{ color: 'var(--brand-accent)' }}>
                <Icon name="nav" size={22} color="var(--brand-accent)" />
              </a>
            )}
          </div>
          {call.pickup_location && <Row label="Pickup" value={call.pickup_location} />}
          {call.dropoff_location && <Row label="Drop-off" value={call.dropoff_location} />}
        </div>

        <div className="m-card" style={{ marginBottom: 10, paddingTop: 4, paddingBottom: 4 }}>
          <Row label="Vehicle" value={call.vehicle_info} />
          <Row label="Reason" value={call.call_reason_name} />
          <Row label="Contact" value={call.contact_name} />
          <Row label="Phone" value={call.contact_number} href={call.contact_number ? `tel:${String(call.contact_number).replace(/[^\d+]/g, '')}` : null} />
          <Row label="Waiting" value={<TimeInQueue createdAt={call.created_at} />} />
          <Row label="Created" value={`${timeAgo(call.created_at)} by ${call.created_by_name || '—'}`} />
          {call.claimed_by_name && <Row label="Claimed" value={`${call.claimed_by_name}${call.claimed_at ? ` · ${timeAgo(call.claimed_at)}` : ''}`} />}
          {call.resolved_at && <Row label="Resolved" value={`${call.resolution_type || ''} · ${call.resolution_reason_name || ''}${call.resolved_by_name ? ` · ${call.resolved_by_name}` : ''}`} />}
          {call.resolution_notes && <Row label="Resolution notes" value={call.resolution_notes} />}
        </div>
        {call.notes && (
          <div className="m-note" style={{ marginBottom: 10 }}><Icon name="alert" size={16} color="#a16207" /><div><strong>Notes:</strong> {call.notes}</div></div>
        )}

        <div className="m-section">Messages{comments.length > 0 ? ` · ${comments.length}` : ''}</div>
        <div className="m-card" style={{ marginBottom: 10 }}>
          {thread}
          {canComment && (
            <div style={{ marginTop: comments.length ? 12 : 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {call.status === 'Claimed' && (
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  {isDriver ? 'Your message is texted to whoever created the call.' : 'Your message is texted to the driver.'}
                </div>
              )}
              {isDriver && isMyClaimedCall && (
                <div style={{ display: 'flex', gap: 8 }}>
                  {presetBtn('ETA 30 min', 'ETA: 30 Minutes')}
                  {presetBtn('ETA 60 min', 'ETA: 60 Minutes')}
                </div>
              )}
              <form className="comment-form" onSubmit={handleSendOther} style={{ display: 'flex', gap: 8 }}>
                <input type="text" className="comment-text-input" value={commentText} onChange={e => setCommentText(e.target.value)}
                  placeholder="Type a message…" disabled={sending} style={{ flex: 1, fontSize: 16, minHeight: 48, borderRadius: 12 }} />
                <button type="submit" className="m-btn" disabled={sending || !commentText.trim()}
                  style={{ width: 'auto', padding: '0 16px', height: 48, background: 'var(--brand-accent)', color: '#fff' }}>Send</button>
              </form>
            </div>
          )}
          {call.status === 'Resolved' && <p className="comment-closed" style={{ marginTop: 8 }}>This call is resolved. Messages are closed.</p>}
        </div>

        <CallPhotosSection call={call} currentUser={currentUser} onChange={(photos) => setCall(prev => prev ? { ...prev, photos } : prev)} />

        {(canClaim || canResolve) && (
          <StickyAction>
            {canClaim && (
              <button type="button" className="m-btn" onClick={handleClaim} disabled={claiming}
                style={{ background: 'var(--brand-accent)', color: '#fff' }}>
                {claiming ? 'Claiming…' : 'Claim this call'}
              </button>
            )}
            {canResolve && (isDueTimePending ? (
              <button type="button" className="m-btn m-btn-secondary" disabled>Resolve opens at {dueTimeDisplay} CT</button>
            ) : (
              <button type="button" className="m-btn" onClick={() => setShowResolve(true)} style={{ background: '#16a34a', color: '#fff' }}>
                <Icon name="check" size={22} color="#fff" /> Resolve call
              </button>
            ))}
          </StickyAction>
        )}
        {showResolve && (
          <ResolveCallModal call={call} resolutionReasons={resolutionReasons}
            onClose={() => setShowResolve(false)}
            onResolved={async () => { setShowResolve(false); await loadCall(); }} />
        )}
      </MobilePage>
    );
  }

  return (
    <div className="call-detail">
      <button className="btn btn-muted" onClick={goBack} style={{marginBottom:16}}>Back</button>

      <div className="detail-header">
        <h2>Call #{call.id} &mdash; {call.property_name || call.call_type_name || 'Call'}</h2>
        <span className="status-badge" style={{ background: STATUS_COLORS[call.status] }}>{call.status}</span>
      </div>

      <div className="detail-grid">
        <div className="detail-card">
          <h3>Call Information</h3>
          <dl>
            <dt>Region</dt><dd>{call.region}</dd>
            <dt>Location</dt><dd>{call.location}</dd>
            {call.pickup_location && <><dt>Pickup</dt><dd>{call.pickup_location}</dd></>}
            {call.dropoff_location && <><dt>Drop-off</dt><dd>{call.dropoff_location}</dd></>}
            <dt>Call Type</dt><dd>{call.call_type_name}</dd>
            <dt>Call Reason</dt><dd>{call.call_reason_name}</dd>
            {call.space_number && <><dt>Space #</dt><dd>{call.space_number}</dd></>}
            {call.vehicle_info && <><dt>Vehicle</dt><dd>{call.vehicle_info}</dd></>}
            {call.contact_name && <><dt>Contact Name</dt><dd>{call.contact_name}</dd></>}
            {call.contact_number && <><dt>Contact Number</dt><dd>{call.contact_number}</dd></>}
            {call.notes && <><dt>Notes</dt><dd>{call.notes}</dd></>}
          </dl>
        </div>

        <div className="detail-card">
          <h3>Timeline</h3>
          <dl>
            <dt>Created</dt><dd>{formatDateTime(call.created_at)} by {call.created_by_name}</dd>
            <dt>Time in Queue</dt><dd><TimeInQueue createdAt={call.created_at} /></dd>
            {call.claimed_by_name && <><dt>Claimed</dt><dd>{formatDateTime(call.claimed_at)} by {call.claimed_by_name}</dd></>}
            {call.resolved_at && <>
              <dt>Resolved</dt><dd>{formatDateTime(call.resolved_at)} by {call.resolved_by_name}</dd>
              <dt>Resolution Type</dt><dd style={{textTransform:'capitalize'}}>{call.resolution_type}</dd>
              <dt>Resolution Reason</dt><dd>{call.resolution_reason_name}</dd>
              {call.resolution_notes && <><dt>Resolution Notes</dt><dd>{call.resolution_notes}</dd></>}
              <dt>Resolution Time</dt><dd>{call.resolution_time_minutes} minutes</dd>
            </>}
          </dl>
        </div>
      </div>

      {/* Communication Thread */}
      <div className="detail-card comment-section">
        <h3>Communication {comments.length > 0 && <span className="comment-count-inline">({comments.length})</span>}</h3>

        {thread}

        {/* Input area */}
        {canComment && (
          <div className="comment-input-area">
            {call.status === 'Claimed' && (
              <div className="comment-sms-hint">
                {isDriver
                  ? '💬 Your message will be texted to the call creator'
                  : '💬 Your message will be texted to the driver'
                }
              </div>
            )}

            {isDriver && isMyClaimedCall ? (
              /* Driver view: preset buttons + Other */
              <div className="comment-driver-actions">
                <div className="preset-buttons mobile-stack">
                  <button
                    className="btn btn-preset"
                    onClick={() => handlePreset('ETA: 30 Minutes')}
                    disabled={sending}
                  >
                    ETA: 30 Min
                  </button>
                  <button
                    className="btn btn-preset"
                    onClick={() => handlePreset('ETA: 60 Minutes')}
                    disabled={sending}
                  >
                    ETA: 60 Min
                  </button>
                  <button
                    className={`btn ${showOtherInput ? 'btn-primary' : 'btn-muted'}`}
                    onClick={() => setShowOtherInput(!showOtherInput)}
                    disabled={sending}
                  >
                    Other
                  </button>
                </div>
                {showOtherInput && (
                  <form className="comment-form" onSubmit={handleSendOther}>
                    <input
                      type="text"
                      className="comment-text-input"
                      value={commentText}
                      onChange={e => setCommentText(e.target.value)}
                      placeholder="Type a message..."
                      autoFocus
                      disabled={sending}
                    />
                    <button type="submit" className="btn btn-primary" disabled={sending || !commentText.trim()}>
                      {sending ? 'Sending...' : 'Send'}
                    </button>
                  </form>
                )}
              </div>
            ) : (
              /* Dispatcher/Admin/Sales view: text input always visible */
              <form className="comment-form" onSubmit={handleSendOther}>
                <input
                  type="text"
                  className="comment-text-input"
                  value={commentText}
                  onChange={e => setCommentText(e.target.value)}
                  placeholder="Add a message to this call..."
                  disabled={sending}
                />
                <button type="submit" className="btn btn-primary" disabled={sending || !commentText.trim()}>
                  {sending ? 'Sending...' : 'Send'}
                </button>
              </form>
            )}
          </div>
        )}

        {call.status === 'Resolved' && comments.length > 0 && (
          <p className="comment-closed">This call has been resolved. Communication is closed.</p>
        )}
      </div>

      <CallPhotosSection
        call={call}
        currentUser={currentUser}
        onChange={(photos) => setCall(prev => prev ? { ...prev, photos } : prev)}
      />
    </div>
  );
}

/**
 * Photos section — always rendered on the detail page. Shows the gallery
 * when photos exist. Shows an Add Photo button for users with a role that
 * can manage calls (super_admin, admin, dispatcher, sales) so photos can
 * be backfilled on calls that arrived without them (e.g. Take Back calls
 * spawned before Apr 22 photo-copy fix, or a dispatcher realizing they
 * forgot to attach the VIN plate after create).
 *
 * Delete (trash) button on each photo for the same management roles.
 * Drivers still upload their own photos via the resolve flow and can
 * view the gallery, but can't edit existing photos here.
 */
function CallPhotosSection({ call, currentUser, onChange }) {
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const canManage = !!currentUser && ['super_admin', 'admin', 'dispatcher', 'sales'].includes(currentUser.role);
  const photos = Array.isArray(call?.photos) ? call.photos : [];

  async function handleFiles(files) {
    if (!files || files.length === 0) return;
    setError('');
    setUploading(true);
    try {
      const form = new FormData();
      for (const f of files) form.append('photos', f);
      const res = await api.addCallPhotos(call.id, form);
      if (res && Array.isArray(res.photos)) onChange(res.photos);
    } catch (err) {
      setError(err?.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  async function handleDelete(photoId) {
    if (!window.confirm('Delete this photo? This cannot be undone.')) return;
    setError('');
    setDeletingId(photoId);
    try {
      await api.deleteCallPhoto(call.id, photoId);
      onChange(photos.filter(p => p.id !== photoId));
    } catch (err) {
      setError(err?.message || 'Delete failed');
    } finally {
      setDeletingId(null);
    }
  }

  // Don't render at all for drivers when there are no photos — they have
  // no upload path on this screen and empty section is noise.
  if (!canManage && photos.length === 0) return null;

  return (
    <div className="detail-card">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <h3 style={{ margin: 0 }}>Photos</h3>
        {canManage && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => handleFiles(e.target.files)}
              style={{ display: 'none' }}
            />
            <button
              type="button"
              className="btn btn-sm btn-outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? 'Uploading…' : '+ Add Photo'}
            </button>
          </>
        )}
      </div>

      {error && <div className="alert alert-error" style={{ marginBottom: 10 }}>{error}</div>}

      {photos.length === 0 ? (
        <div style={{ fontSize: 13, color: '#94a3b8', fontStyle: 'italic', padding: '8px 0' }}>
          No photos yet.{canManage ? ' Click "+ Add Photo" to upload.' : ''}
        </div>
      ) : (
        <PhotoGallery
          photos={photos}
          onDelete={canManage ? handleDelete : null}
          deletingId={deletingId}
        />
      )}
    </div>
  );
}
